import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { novoRegistroBase } from '../../db/tipos'
import { gravarLocalEEnfileirar } from '../../sync/outbox'
import { confirmar } from '../../componentes/confirmar'

const estiloCampo = 'w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white'

// Pedido dela 10/10: a lista mostra SO as fornecedoras que tem codigo para este
// cliente; "+" acrescenta uma a mao, lapis edita o codigo, lixeira apaga.
// Apagar zera o CODIGO e mantem o vinculo: a mesma linha guarda a tabela de
// preco que o pedido lembrou para esta marca.
export default function VinculosRepresentadas({ clienteId }: { clienteId: string }) {
  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'),
    [],
  )
  const vinculos = useLiveQuery(
    () =>
      banco.clientes_representadas
        .where('cliente_id')
        .equals(clienteId)
        .filter((v) => v.apagado_em === null)
        .toArray(),
    [clienteId],
  )

  const [adicionando, setAdicionando] = useState(false)
  const [novaRepresentada, setNovaRepresentada] = useState('')
  const [novoCodigo, setNovoCodigo] = useState('')
  const [editando, setEditando] = useState<string | null>(null)
  const [codigoEditado, setCodigoEditado] = useState('')

  async function salvarCodigo(representadaId: string, codigo: string) {
    const existente = vinculos?.find((v) => v.representada_id === representadaId)
    const valor = codigo.trim() || null
    if (existente) {
      if (existente.codigo_cliente_na_representada === valor) return
      await gravarLocalEEnfileirar('clientes_representadas', {
        ...existente, codigo_cliente_na_representada: valor,
      })
    } else if (valor !== null) {
      await gravarLocalEEnfileirar('clientes_representadas', {
        ...novoRegistroBase(),
        cliente_id: clienteId,
        representada_id: representadaId,
        codigo_cliente_na_representada: valor,
        tabela_preco_padrao_id: null,
        observacoes: null,
      })
    }
  }

  const cabecalho = (
    <div>
      <h3 className="font-bold text-slate-800">Códigos do cliente por fornecedora</h3>
      <p className="text-sm text-slate-500">Informe os códigos que este cliente possui em cada fornecedora. Eles saem no PDF do pedido.</p>
    </div>
  )

  // undefined = ainda nao carregou; array (mesmo vazio) = carregou. Nao desenhar
  // antes disso: uma lista vazia por engano convidaria a "adicionar" um codigo
  // que ja existe.
  if (vinculos === undefined || representadas === undefined) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        {cabecalho}
        <p className="text-xs text-slate-500">Carregando...</p>
      </div>
    )
  }

  const comCodigo = vinculos
    .filter((v) => (v.codigo_cliente_na_representada ?? '').trim() !== '')
    .map((v) => ({ vinculo: v, nome: representadas.find((r) => r.id === v.representada_id)?.nome ?? '' }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  const idsComCodigo = new Set(comCodigo.map((l) => l.vinculo.representada_id))
  const semCodigo = representadas.filter((r) => !idsComCodigo.has(r.id))

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3" data-testid="codigos-fornecedora">
      <div className="flex items-start justify-between gap-3">
        {cabecalho}
        <button
          type="button"
          aria-label="Adicionar código"
          disabled={semCodigo.length === 0}
          onClick={() => { setAdicionando(true); setNovaRepresentada(semCodigo[0]?.id ?? ''); setNovoCodigo('') }}
          className="shrink-0 w-9 h-9 rounded-lg border border-teal-700 text-teal-700 text-xl leading-none disabled:opacity-40"
        >
          +
        </button>
      </div>

      {adicionando && (
        <div className="bg-teal-50 rounded-lg p-3 space-y-2">
          <select
            aria-label="Fornecedora do novo código"
            value={novaRepresentada}
            onChange={(e) => setNovaRepresentada(e.target.value)}
            className={estiloCampo}
          >
            {semCodigo.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
          </select>
          <input
            aria-label="Código do cliente na fornecedora"
            placeholder="Código do cliente"
            value={novoCodigo}
            onChange={(e) => setNovoCodigo(e.target.value)}
            // Mora dentro do <form> do cliente: Enter ("Ir" no celular)
            // salvava o cliente e perdia o codigo (code review 11/10).
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              if (novaRepresentada && novoCodigo.trim() !== '') {
                void salvarCodigo(novaRepresentada, novoCodigo).then(() => setAdicionando(false))
              }
            }}
            className={estiloCampo}
          />
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdicionando(false)}
              className="flex-1 border border-slate-300 text-slate-600 rounded-lg py-1.5 text-sm bg-white">
              Cancelar
            </button>
            <button
              type="button"
              disabled={!novaRepresentada || novoCodigo.trim() === ''}
              onClick={() => void salvarCodigo(novaRepresentada, novoCodigo).then(() => setAdicionando(false))}
              className="flex-1 bg-teal-700 text-white rounded-lg py-1.5 text-sm disabled:opacity-40"
            >
              Adicionar
            </button>
          </div>
        </div>
      )}

      {comCodigo.length === 0 && !adicionando && (
        <p className="text-sm text-slate-500">Nenhum código informado. Toque no + para adicionar.</p>
      )}

      {comCodigo.map(({ vinculo, nome }) => {
        const emEdicao = editando === vinculo.representada_id
        return (
          <div key={vinculo.id} className="flex items-center gap-2 border-t border-slate-100 pt-2" data-testid="linha-codigo">
            <span className="flex-1 min-w-0 text-sm text-slate-700 break-words">{nome}</span>
            {emEdicao ? (
              <>
                <input
                  aria-label={`Código em ${nome}`}
                  value={codigoEditado}
                  onChange={(e) => setCodigoEditado(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    if (codigoEditado.trim() !== '') {
                      void salvarCodigo(vinculo.representada_id, codigoEditado).then(() => setEditando(null))
                    }
                  }}
                  className={`${estiloCampo} w-32`}
                />
                <button type="button" aria-label={`Salvar código de ${nome}`}
                  disabled={codigoEditado.trim() === ''}
                  onClick={() => void salvarCodigo(vinculo.representada_id, codigoEditado).then(() => setEditando(null))}
                  className="px-2 py-1 rounded-lg bg-teal-700 text-white text-sm disabled:opacity-40">
                  ✓
                </button>
                <button type="button" aria-label="Cancelar edição" onClick={() => setEditando(null)}
                  className="px-2 py-1 rounded-lg border border-slate-300 text-slate-600 text-sm">
                  ✕
                </button>
              </>
            ) : (
              <>
                <span className="text-sm font-medium text-slate-800">{vinculo.codigo_cliente_na_representada}</span>
                <button type="button" aria-label={`Editar código de ${nome}`}
                  onClick={() => { setEditando(vinculo.representada_id); setCodigoEditado(vinculo.codigo_cliente_na_representada ?? '') }}
                  className="p-1.5 text-teal-700">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" /></svg>
                </button>
                <button type="button" aria-label={`Apagar código de ${nome}`}
                  onClick={() => {
                    void confirmar(`Apagar o código de ${nome}?`, { botao: 'Apagar', perigo: true }).then((sim) => {
                      if (sim) void salvarCodigo(vinculo.representada_id, '')
                    })
                  }}
                  className="p-1.5 text-red-600">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" /></svg>
                </button>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}
